import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {NON_STANDARD_AMINO_ACIDS} from 'biojs/Codons';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import {type IntPropertyNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {
  PROTEIN_AMINO_ACIDS_QUERY_WRAPPER,
  MOLECULE_HEAVY_ATOM_COUNT_QUERY_WRAPPER,
} from 'model-hub/ModelHubQueries';
import ComponentSearchModal from 'model-hub/scientific-models/input-forms/ComponentSearchModal';
import {
  getMaxAminoAcidCount,
  getMinAminoAcidCount,
  getMaxMultimerComponents,
  getMaxTokenCount,
  supportsMulitmerMolecules,
  supportsMultimers,
} from 'model-hub/scientific-models/ScientificModelConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER, GRAY_LIGHT, GRAY_LIGHTER, GREEN, RED} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';

import {
  calculateTotalAminoAcidCount,
  isComponentSelectionValid,
  getComponentValidationResult,
  addComponents,
  incrementComponent,
  decrementComponent,
  removeComponent,
  SelectedComponents,
  ComponentValidationResult,
  ValidationError,
  getComponentCount,
  calculateTotalHeavyAtomCount,
  calculateTotalTokenCount,
} from './ComponentUtils';
import SelectedComponent from './SelectedComponent';

function ValidationErrorMessage({error, model}: {error: ValidationError; model: string}): JSX.Element {
  switch (error.type) {
    case 'no_components':
      return <span>Please select at least one component.</span>;

    case 'no_aa_sequence_components':
      return <span>Please select at least one AA sequence component.</span>;

    case 'empty_amino_acids': {
      const emptyCount = error.componentIds.length;
      return (
        <span>
          {pluralize(emptyCount, 'This component does', 'These components do')} not contain any residues:{' '}
          {error.componentIds.map((componentId, index) => (
            <span key={componentId}>
              {index > 0 && ', '}
              <BenchChip id={componentId} size={ChipSize.MINI} isLink={false} />
            </span>
          ))}
        </span>
      );
    }

    case 'non_standard_amino_acids': {
      const nonStandardCount = error.componentIds.length;
      return (
        <span>
          {pluralize(nonStandardCount, 'This component has', 'These components have')} non-standard amino
          acids ({NON_STANDARD_AMINO_ACIDS.join(', ')}):{' '}
          {error.componentIds.map((componentId, index) => (
            <span key={componentId}>
              {index > 0 && ' '}
              <BenchChip id={componentId} size={ChipSize.MINI} isLink={false} />
            </span>
          ))}
        </span>
      );
    }

    case 'count_exceeded':
      return (
        <span>
          Total amino acid count ({error.totalCount}) exceeds the limit of {getMaxAminoAcidCount(model)}.
        </span>
      );

    case 'count_too_low': {
      const minCount = getMinAminoAcidCount(model);
      return (
        <span>
          Total amino acid count ({error.totalCount}) is below the minimum of {minCount}.
        </span>
      );
    }

    case 'too_many_components':
      return (
        <span>
          Number of components ({error.componentCount}) exceeds the limit of {getMaxMultimerComponents(model)}
          .
        </span>
      );

    case 'too_many_tokens': {
      const maxTokenCount = getMaxTokenCount(model);
      return (
        <span>
          Total token count ({error.tokenCount}) exceeds the limit of {maxTokenCount}. Tokens are calculated
          by the total number of residues in amino acid sequences and heavy atom count in molecules.
        </span>
      );
    }

    case 'insufficient_permissions': {
      const insufficientCount = error.componentIds.length;
      return (
        <span>
          You do not have sufficient permissions to run structure prediction on{' '}
          {pluralize(insufficientCount, 'this component', 'these components')}:{' '}
          {error.componentIds.map((componentId, index) => (
            <span key={componentId}>
              {index > 0 && ', '}
              <BenchChip id={componentId} size={ChipSize.MINI} isLink={false} />
            </span>
          ))}
        </span>
      );
    }

    default:
      assertNever(error);
      return <></>;
  }
}

function ValidationMessages({
  validationResult,
  model,
}: {
  validationResult: ComponentValidationResult;
  model: string;
}): JSX.Element {
  return (
    <div>
      {validationResult.map((error, index) => (
        <div
          key={`${error.type}-${index}`}
          className={css(styles.validationError)}
          {...getTestProps({
            component: 'ComponentSelector',
            element: 'validationMessage',
            key: `${error.type}-${index}`,
          })}
        >
          <ValidationErrorMessage error={error} model={model} />
        </div>
      ))}
    </div>
  );
}

export interface ComponentSelectorProps {
  selectedComponents: SelectedComponents;
  onComponentsChange: (components: SelectedComponents) => void;
  primaryProteinId?: string | null;
  model: string;
  disabled?: boolean;
}

export default function ComponentSelector({
  selectedComponents,
  onComponentsChange,
  primaryProteinId,
  model,
  disabled = false,
}: ComponentSelectorProps): JSX.Element {
  const modelSupportsMultimers = supportsMultimers(model);
  const modelSupportsMultimerMolecules = supportsMulitmerMolecules(model);
  const rowSelectionMode = modelSupportsMultimers ? RowSelectionMode.MULTIPLE : RowSelectionMode.SINGLE;

  const availableTypes: Array<OligoAwareFolderItemType> = useMemo(
    () =>
      BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && modelSupportsMultimerMolecules
        ? [ModelTypeConstants.PROTEIN, ModelTypeConstants.MOLECULE]
        : [ModelTypeConstants.PROTEIN],
    [modelSupportsMultimerMolecules]
  );

  const handleOpenPolymerSearch = async (): Promise<void> => {
    const selectedComponentIds = await legacyRunModal(ComponentSearchModal, {
      rowSelectionMode,
      availableTypes,
      title: modelSupportsMultimers ? 'Select components' : 'Select component',
    });

    if (selectedComponentIds == null) {
      // Modal was closed without submitting
      return;
    }

    // Filter out AA sequences that are already selected to avoid duplicates
    const newBioentityIds = selectedComponentIds.filter((id: string) => selectedComponents[id] == null);

    if (newBioentityIds.length === 0) {
      return; // No new bioentities to add
    }

    const newProteinIds = newBioentityIds.filter((id: string) => APIUtil.isProtein(id));

    // Fetch amino acids for the selected AA sequences
    const result = await doQuerySafe(PROTEIN_AMINO_ACIDS_QUERY_WRAPPER, {
      variables: {ids: newProteinIds},
    });

    let updatedComponents = {...selectedComponents};

    if (result) {
      const newComponents = accessibleObjs(result.proteins).map((protein) => ({
        id: protein.id,
        aminoAcids: protein.experimentalCurrentVersion?.aminoAcids || '',
        entityType: ModelTypeConstants.PROTEIN,
        numHeavyAtoms: 0,
        permissions: {
          readable: protein.permissions.readable,
          polymerFeaturesEditable: protein.permissions.polymerFeaturesEditable,
        },
      }));

      updatedComponents = addComponents(updatedComponents, newComponents);
    }

    if (BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
      const newMoleculeIds = newBioentityIds.filter((id: string) => APIUtil.isMolecule(id));

      // Fetch heavy atom count for the selected molecules
      const moleculeResult = await doQuerySafe(MOLECULE_HEAVY_ATOM_COUNT_QUERY_WRAPPER, {
        variables: {moleculeIds: newMoleculeIds},
      });

      if (moleculeResult) {
        const newComponents = accessibleObjs(moleculeResult.molecules).map((molecule) => ({
          id: molecule.id,
          aminoAcids: '',
          entityType: ModelTypeConstants.MOLECULE,
          // This could throw an error if the BE service is unable to calculate the heavyAtomCount
          // but this is unlikely to happen in practice because we don't allow users to add molecules that are not valid
          numHeavyAtoms: (molecule.currentVersion.chemicalStructure.heavyAtomCount as IntPropertyNode).value,
          permissions: {
            readable: molecule.permissions.readable,
          },
        }));
        updatedComponents = addComponents(updatedComponents, newComponents);
      }
    }
    onComponentsChange(updatedComponents);
  };
  const handleIncrementComponent = (componentId: string): void => {
    const updatedComponents = incrementComponent(selectedComponents, componentId);
    onComponentsChange(updatedComponents);
  };

  const handleDecrementComponent = (componentId: string): void => {
    // Don't allow the primary AA sequence to be decremented below 1
    // only check if primaryProteinId is provided
    if (primaryProteinId && componentId === primaryProteinId) {
      const primaryComponent = selectedComponents[primaryProteinId];
      if (primaryComponent && primaryComponent.count <= 1) {
        return;
      }
    }
    const updatedComponents = decrementComponent(selectedComponents, componentId);
    onComponentsChange(updatedComponents);
  };

  const handleRemoveComponent = (componentId: string): void => {
    // Don't allow the primary AA sequence to be completely removed
    // only check if primaryProteinId is provided
    if (primaryProteinId && componentId === primaryProteinId) {
      return;
    }
    const updatedComponents = removeComponent(selectedComponents, componentId);
    onComponentsChange(updatedComponents);
  };
  const totalAminoAcidCount = calculateTotalAminoAcidCount(selectedComponents);
  const totalHeavyAtomCount = calculateTotalHeavyAtomCount(selectedComponents);
  const totalTokenCount = calculateTotalTokenCount(selectedComponents);
  const isValidSelection = isComponentSelectionValid(selectedComponents, model);
  const validationResult = getComponentValidationResult(selectedComponents, model);
  const totalComponentCount = getComponentCount(selectedComponents);
  const isAtComponentLimit = totalComponentCount >= getMaxMultimerComponents(model);

  const titleText = `Select ${pluralize(getMaxMultimerComponents(model), 'component')}`;
  const singularComponentHelpText = (
    <>
      Search for and select a structure prediction component to include in your structure prediction. The
      total amino acid count cannot exceed {getMaxAminoAcidCount(model)}.
    </>
  );
  const pluralComponentHelpText = (
    <>
      Search for and select structure prediction components to include in your structure prediction. The total
      amino acid count across all components cannot exceed {getMaxAminoAcidCount(model)}.
    </>
  );
  const helpText = (
    <>
      {getMaxMultimerComponents(model) > 1 ? pluralComponentHelpText : singularComponentHelpText}
      {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && supportsMulitmerMolecules(model) && (
        <>
          {' '}
          There is no limit on the size of individual molecules, however the total token count across all
          components cannot exceed {getMaxTokenCount(model)}. Tokens are calculated as the total number of
          residues in amino acid sequences and heavy (non-hydrogen) atom count in molecules.
        </>
      )}
    </>
  );
  return (
    <div className={css(styles.containerWrapper)}>
      <div className={css(styles.componentSelectorContainer)}>
        <div className={css(styles.label)}>{titleText}</div>
        <div className={css(styles.helpText)}>{helpText}</div>

        <div className={css(styles.selectedComponentsSection)}>
          <div className={css(styles.sectionHeader)}>
            <div className={css(styles.sectionTitle)}>{titleText}</div>

            <div className={css(styles.addComponentSection)}>
              <Tooltip
                text={`Maximum of ${pluralize(getMaxMultimerComponents(model), 'component')} allowed`}
                isDisabled={!isAtComponentLimit}
              >
                <Button
                  size={ButtonSize.MEDIUM}
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  text={`Add ${pluralize(getMaxMultimerComponents(model), 'component')}`}
                  onClick={handleOpenPolymerSearch}
                  disabled={disabled || isAtComponentLimit}
                  {...getTestProps({component: 'ComponentSelector', element: 'addButton'})}
                />
              </Tooltip>
            </div>
          </div>

          {Object.keys(selectedComponents).length > 0 && (
            <>
              <div className={css(styles.componentsList)}>
                {Object.entries(selectedComponents).map(([componentId, componentData]) => (
                  <SelectedComponent
                    key={componentId}
                    componentId={componentId}
                    count={componentData.count}
                    isPrimary={componentId === primaryProteinId}
                    disabled={disabled}
                    incrementDisabled={isAtComponentLimit}
                    model={model}
                    onIncrement={handleIncrementComponent}
                    onDecrement={handleDecrementComponent}
                    onRemove={handleRemoveComponent}
                  />
                ))}
              </div>

              <div className={css(styles.totalSection)}>
                <div className={css(styles.totalInfo)}>
                  <span className={css(styles.totalLabel)}>Total amino acids:</span>
                  <span
                    className={css(
                      styles.totalCount,
                      totalAminoAcidCount > getMaxAminoAcidCount(model) && styles.totalCountError
                    )}
                  >
                    {totalAminoAcidCount} / {getMaxAminoAcidCount(model)}
                  </span>
                </div>
                {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && supportsMulitmerMolecules(model) && (
                  <>
                    <div className={css(styles.totalInfo)}>
                      <Tooltip text="Non-hydrogen atoms across all molecules (not including protein entities).">
                        <span className={css(styles.totalLabel)}>Total heavy atoms:</span>
                      </Tooltip>
                      <span className={css(styles.totalCountNeutral)}>{totalHeavyAtomCount}</span>
                    </div>
                    <div className={css(styles.totalInfo)}>
                      <Tooltip text="Tokens are calculated as the total number of residues in amino acid sequences and heavy atom count in molecules.">
                        <span className={css(styles.totalLabel)}>Total tokens:</span>
                      </Tooltip>
                      <span className={css(styles.totalCount, !isValidSelection && styles.totalCountError)}>
                        {totalTokenCount} / {getMaxTokenCount(model)}
                      </span>
                    </div>
                  </>
                )}
                {validationResult && (
                  <div
                    className={css(styles.validationMessage)}
                    {...getTestProps({
                      component: 'ComponentSelector',
                      element: 'validationMessagesWrapper',
                    })}
                  >
                    <ValidationMessages validationResult={validationResult} model={model} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  containerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20px 0px',
    padding: '0px 16px',
  },
  componentSelectorContainer: {
    minWidth: '400px',
    maxWidth: '600px',
    textAlign: 'left',
  },
  label: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
    marginBottom: '8px',
  },
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginBottom: '16px',
    lineHeight: '18px',
  },
  selectedComponentsSection: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
    padding: '16px',
    backgroundColor: GRAY_LIGHTER,
  },
  sectionTitle: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
    marginBottom: '12px',
  },
  addComponentSection: {
    marginBottom: '16px',
  },
  componentsList: {
    marginBottom: '16px',
  },
  totalSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingTop: '12px',
  },
  totalInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  totalLabel: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
  },
  totalCount: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
    color: GREEN,
  },
  totalCountNeutral: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
  },
  totalCountError: {
    color: RED,
  },
  validationMessage: {
    ...FONT_PROXIMA_13,
    color: RED,
    fontSize: '12px',
  },
  validationError: {
    marginBottom: '4px',
    ':last-child': {
      marginBottom: '0px',
    },
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
